/**
 * AI 聊天模态框主组件
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useChatStore, useServerStore, useThemeStore, useFileEditorStore } from '@/stores';
import { useIsLightUi } from '@/hooks';
import { modalOverlayInline, mainCardInline } from '@/utils/themeInline';
import { useMessages } from '@/hooks';
import { loadMessages, saveMessages } from '@/utils/storage';
import { formatAgentToolLine } from '@/utils/toolDisplayLabels';
import {
  ChatHeader,
  ServerBar,
  MessageList,
  InputArea,
  ConfirmationDialog,
  InteractiveDialog,
  FileManagerPanel,
  FileEditorPanel,
} from '@/components';
import type { WSState } from '@/types';

type WsClientType = {
  sessionId: string;
  on: (event: string, callback: (data: unknown) => void) => void;
  off: (event: string, callback?: (data: unknown) => void) => void;
  socket?: {
    emit: (event: string, data?: unknown) => void;
    on: (event: string, callback: (data: unknown) => void) => void;
    off: (event: string, callback?: (data: unknown) => void) => void;
  };
  is_connected: () => boolean;
  connect: () => Promise<void>;
  close: () => void;
};

interface AiChatModalProps {
  serverId: number;
  serverName: string;
  serverIp: string;
  onClose: () => void;
}

/** 断开 WS：中止进行中的 Agent，但不清服务端会话历史（否则关弹窗后再开「继续」会丢记忆） */
function teardownWebSocket(client: WsClientType | null) {
  if (!client) return;
  try {
    client.socket?.emit('abort_chat');
  } catch {
    /* ignore */
  }
  try {
    client.close();
  } catch {
    /* ignore */
  }
}

export const AiChatModal: React.FC<AiChatModalProps> = ({
  serverId,
  serverName,
  serverIp,
  onClose,
}) => {
  const { isModalOpen, isFullscreen, setActiveServer, openModal, closeModal } = useServerStore();
  const { clearMessages, sessionId, generateSessionId } = useChatStore();
  const replaceMessages = useChatStore((s) => s.replaceMessages);
  const setIsProcessing = useChatStore((s) => s.setIsProcessing);
  const setPendingMessage = useChatStore((s) => s.setPendingMessage);
  const hideConfirmation = useChatStore((s) => s.hideConfirmation);
  const hideInteractivePrompt = useChatStore((s) => s.hideInteractivePrompt);

  // 文件编辑器状态
  const editorOpen = useFileEditorStore((s) => s.editorOpen);
  const openFile = useFileEditorStore((s) => s.openFile);
  const closeEditor = useFileEditorStore((s) => s.closeEditor);
  const isDirty = useFileEditorStore((s) =>
    s.openFile ? s.openFile.content !== s.openFile.originalContent : false
  );

  const [wsClient, setWsClient] = useState<WsClientType | null>(null);
  const [wsState, setWsState] = useState<WSState>('CLOSED');
  const [storageReady, setStorageReady] = useState(false);
  const [fileManagerOpen, setFileManagerOpen] = useState(false);
  /** 必须用 ref：effect 清理时 state 里的 wsClient 往往还是 null（异步 setState 尚未提交） */
  const wsClientRef = useRef<WsClientType | null>(null);
  /** 用户点「清空」后递增，避免尚未完成的 loadMessages 把旧记录又 replace 回来 */
  const historyLoadGenerationRef = useRef(0);

  /**
   * 将服务端 conversationHistory（含 role:tool / assistant+tool_calls）转换为前端 Message 格式
   * tool 和 assistant+tool_calls 消息转为 system/assistant 气泡，保证用户可见 AI 做了什么
   */
  const convertServerHistory = useRef((history: any[]): any[] => {
    const result: any[] = [];
    for (let i = 0; i < history.length; i++) {
      const m = history[i];

      // assistant + tool_calls：转为系统消息显示工具调用摘要
      if (m.role === 'assistant' && m.tool_calls && m.tool_calls.length) {
        for (const tc of m.tool_calls) {
          const toolName = tc.function?.name || tc.function?.name || '';
          const toolArgs = tc.function?.arguments || '{}';
          let args: Record<string, string> = {};
          try { args = JSON.parse(toolArgs); } catch {}

          if (toolName === 'execute_command') {
            result.push({
              id: `hist_tc_${i}_${tc.id}`,
              role: 'system',
              content: `✓ ${args.command || '(命令)'}`,
              timestamp: m.timestamp || Date.now(),
            });
          } else if (toolName) {
            result.push({
              id: `hist_tc_${i}_${tc.id}`,
              role: 'system',
              content: formatAgentToolLine(toolName),
              timestamp: m.timestamp || Date.now(),
            });
          }
        }
        // 如果 assistant 还有文字内容（thought），也保留
        if (m.content && String(m.content).trim()) {
          result.push({
            id: `hist_thought_${i}`,
            role: 'assistant',
            content: String(m.content).trim(),
            assistantKind: 'tool_output',
            timestamp: m.timestamp || Date.now(),
          });
        }
        continue;
      }

      // role: tool — 转为助手消息显示结果
      if (m.role === 'tool') {
        let content: string;
        try {
          const parsed = typeof m.content === 'string' ? JSON.parse(m.content) : m.content;
          if (parsed && typeof parsed === 'object') {
            if (parsed.success === false && parsed.error) {
              content = `✗ ${parsed.error}`;
            } else if (parsed.output) {
              content = typeof parsed.output === 'string' ? parsed.output : JSON.stringify(parsed.output, null, 2);
            } else if (parsed.message) {
              content = String(parsed.message);
            } else if (parsed.data) {
              content = typeof parsed.data === 'string' ? parsed.data : JSON.stringify(parsed.data, null, 2);
            } else {
              content = JSON.stringify(parsed, null, 2);
            }
          } else {
            content = String(m.content);
          }
        } catch {
          content = String(m.content);
        }
        if (content.length > 5000) {
          content = `${content.slice(0, 5000)}\n... (已截断)`;
        }
        result.push({
          id: `hist_tool_${i}_${m.tool_call_id || ''}`,
          role: 'assistant',
          content,
          assistantKind: 'tool_output',
          timestamp: m.timestamp || Date.now(),
        });
        continue;
      }

      // 普通 user / assistant / system 消息：直接保留
      result.push(m);
    }
    return result;
  });

  useEffect(() => {
    setActiveServer(serverId, serverName, serverIp);
    openModal();
    // 避免上次关闭异常导致界面一直显示「处理中」
    setIsProcessing(false);
  }, [serverId, serverName, serverIp, setActiveServer, openModal, setIsProcessing]);

  const syncResolvedTheme = useThemeStore((s) => s.syncResolved);

  // 每次打开弹窗重新解析「自动」主题（关闭期间用户可能改了系统深浅色）
  useEffect(() => {
    if (isModalOpen) {
      syncResolvedTheme();
    }
  }, [isModalOpen, syncResolvedTheme]);

  useEffect(() => {
    let cancelled = false;

    const initConnection = async () => {
      // === 本地优先策略（Cursor 风格）===
      // 1. 立即从 IndexedDB 加载历史（毫秒级）
      setStorageReady(false);
      const localHistory = await loadMessages(serverId);
      if (!cancelled) {
        const normalized = localHistory.map((m, i) => ({
          ...m,
          id: m.id != null && String(m.id).trim() !== '' ? String(m.id) : `hist_${Date.now()}_${i}`,
        }));
        replaceMessages(normalized);
        setStorageReady(true);  // 立即可用！
        console.log('[AI Chat] Loaded local history:', normalized.length, 'messages');
      }

      // 2. 后台异步连接 WebSocket 和同步服务端历史（不阻塞）
      try {
        const WSClientClass = (window as unknown as { WS_Chat_Client: new (opts: object) => WsClientType })
          .WS_Chat_Client;

        if (!WSClientClass) {
          console.warn('[AI Chat] WS_Chat_Client not found, using local only');
          return;
        }

        const sid = useChatStore.getState().sessionId;
        const client: WsClientType = new WSClientClass({
          serverId,
          sessionId: sid,
          wsUrl: window.sseConfig?.wsUrl,
        });

        wsClientRef.current = client;

        client.on('connecting', () => setWsState('CONNECTING'));
        client.on('open', () => setWsState('OPEN'));
        client.on('disconnected', () => setWsState('CLOSED'));
        client.on('error', () => setWsState('ERROR'));

        await client.connect();

        if (cancelled) {
          teardownWebSocket(client);
          wsClientRef.current = null;
          return;
        }

        setWsClient(client);
        setWsState(client.is_connected() ? 'OPEN' : 'CONNECTING');

        // 3. 后台从服务端获取历史（跨设备同步）
        try {
          const serverHistory = await (client as any).getServerHistory?.();
          if (!cancelled && serverHistory && serverHistory.length > localHistory.length) {
            // 服务端历史含 role:tool / assistant+tool_calls，需转为前端 Message 格式
            const converted = convertServerHistory.current(serverHistory);
            const normalized = converted.map((m: any, i: number) => ({
              ...m,
              id: m.id != null && String(m.id).trim() !== '' ? String(m.id) : `srv_${Date.now()}_${i}`,
            }));
            replaceMessages(normalized);
            // 存到 IndexedDB 的也用转换后的格式（前端格式），但服务端仍存原始格式
            await saveMessages(serverId, normalized);
            console.log('[AI Chat] Synced from server:', normalized.length, 'messages (local:', localHistory.length, ')');
          }
        } catch (historyErr) {
          console.warn('[AI Chat] Server history sync failed:', historyErr);
        }

      } catch (error) {
        console.error('[AI Chat] WebSocket connection failed:', error);
        setWsState('ERROR');
        wsClientRef.current = null;
      }
    };

    initConnection();

    return () => {
      cancelled = true;
      const c = wsClientRef.current;
      teardownWebSocket(c);
      wsClientRef.current = null;
      setWsClient(null);
      setWsState('CLOSED');

      const srv = useServerStore.getState().activeServerId;
      const msgs = useChatStore.getState().messages;
      if (srv) {
        saveMessages(srv, msgs).catch(() => {});
      }
    };
  }, [serverId, sessionId]);

  const { sendMessage, sendConfirmationResponse, sendInteractiveInput, abortChat } = useMessages({
    serverId,
    wsClient,
  });

  const branchFromUserMessage = useChatStore((s) => s.branchFromUserMessage);

  const handleUserMessageResend = useCallback(
    (messageId: string, newContent: string) => {
      const client = wsClientRef.current;
      if (!client?.socket) {
        useChatStore.getState().addMessage({
          role: 'system',
          content: '⚠ 未连接到服务器，无法发送。请等待连接建立后再编辑重发。',
        });
        return;
      }
      const ok = branchFromUserMessage(messageId, newContent);
      if (!ok) {
        useChatStore.getState().addMessage({
          role: 'system',
          content: '⚠ 无法从该条消息分支：未在会话列表中找到对应记录（可尝试刷新页面后重试）。',
        });
        return;
      }
      const st = useChatStore.getState();
      st.setIsProcessing(true);
      st.clearAgentLiveUi();
      client.socket.emit('chat', {
        server_id: serverId,
        message: newContent.trim(),
        session_id: st.sessionId,
        mode: st.chatMode,
      });
    },
    [branchFromUserMessage, serverId]
  );

  const handleSend = useCallback(
    (message: string) => {
      sendMessage(message);
    },
    [sendMessage]
  );

  const handleStop = useCallback(() => {
    abortChat();
  }, [abortChat]);

  const handleQuickAction = useCallback(
    (command: string) => {
      sendMessage(command);
    },
    [sendMessage]
  );

  const handleClear = useCallback(() => {
    historyLoadGenerationRef.current += 1;
    // 使用 server_id 清除（跨设备同步）
    try {
      wsClientRef.current?.socket?.emit('clear_session', { server_id: serverId });
    } catch {
      /* ignore */
    }
    clearMessages();
    generateSessionId();
    // 立即覆盖 IndexedDB，否则仅内存清空、重开弹窗仍会 loadMessages 恢复旧对话
    void saveMessages(serverId, []).catch(() => {});
    setStorageReady(true);
  }, [clearMessages, generateSessionId, serverId, setStorageReady]);

  /** 保存文件到远端服务器 */
  const handleSaveFile = useCallback(
    async (content: string): Promise<boolean> => {
      const client = wsClientRef.current;
      if (!client?.socket) return false;
      const filePath = useFileEditorStore.getState().openFile?.path;
      if (!filePath) return false;
      return new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(false), 30000);
        (client.socket as any).once('file_manager_write_result', (res: { success: boolean; error?: string }) => {
          clearTimeout(timeout);
          resolve(res?.success ? true : false);
        });
        (client.socket as any).emit('file_manager_write', { path: filePath, content, server_id: serverId });
      });
    },
    [serverId]
  );

  const handleClose = useCallback(() => {
    const c = wsClientRef.current;
    teardownWebSocket(c);
    wsClientRef.current = null;
    setWsClient(null);
    setWsState('CLOSED');

    setIsProcessing(false);
    setPendingMessage(null);
    hideConfirmation();
    hideInteractivePrompt();

    const srv = useServerStore.getState().activeServerId;
    const msgs = useChatStore.getState().messages;
    if (srv) {
      saveMessages(srv, msgs).catch(() => {});
    }

    closeModal();
    onClose();
  }, [
    closeModal,
    onClose,
    setIsProcessing,
    setPendingMessage,
    hideConfirmation,
    hideInteractivePrompt,
  ]);

  const handleConfirm = useCallback(
    (interruptId: string) => {
      sendConfirmationResponse(interruptId, true);
    },
    [sendConfirmationResponse]
  );

  const handleReject = useCallback(
    (interruptId: string) => {
      sendConfirmationResponse(interruptId, false);
    },
    [sendConfirmationResponse]
  );

  const handleInteractiveSubmit = useCallback(
    (sessId: string, input: string) => {
      sendInteractiveInput(sessId, input);
    },
    [sendInteractiveInput]
  );

  const handleInteractiveCancel = useCallback(
    (sessId: string) => {
      if (wsClient?.socket) {
        wsClient.socket.emit('cancel_interactive', { session_id: sessId });
      }
    },
    [wsClient]
  );

  // 切换服务器：关闭当前 React 实例，用新服务器重新打开
  const handleSwitchServer = useCallback(
    (serverId: number, name: string, ip: string) => {
      // 先保存当前消息
      const srv = useServerStore.getState().activeServerId;
      const msgs = useChatStore.getState().messages;
      if (srv) {
        saveMessages(srv, msgs).catch(() => {});
      }
      // 关闭当前 WS
      const c = wsClientRef.current;
      teardownWebSocket(c);
      wsClientRef.current = null;
      // 通过全局入口重新打开
      if (typeof (window as any).openAiChat === 'function') {
        (window as any).openAiChat(serverId, name, ip);
      }
    },
    []
  );

  const wsStatus: 'connected' | 'connecting' | 'disconnected' | 'error' =
    wsState === 'OPEN'
      ? 'connected'
      : wsState === 'CONNECTING'
        ? 'connecting'
        : wsState === 'ERROR'
          ? 'error'
          : 'disconnected';

  const isLightUi = useIsLightUi();

  if (!isModalOpen) return null;

  return (
    <div
      className={`sse-ai-modal ${isFullscreen ? 'fullscreen' : ''} active`}
      style={modalOverlayInline(isLightUi)}
    >
      <div className="sse-ai-container" style={mainCardInline(isLightUi)}>
        <ChatHeader wsState={wsStatus} onClose={handleClose} onClear={handleClear} onSwitchServer={handleSwitchServer} />
        <ServerBar
          fileManagerOpen={fileManagerOpen}
          onToggleFileManager={() => setFileManagerOpen((v) => !v)}
        />
        <div className="sse-ai-main-row">
          {fileManagerOpen && (
            <div className="sse-ai-file-panel-wrap">
              <FileManagerPanel
                wsClient={wsClient}
                serverId={serverId}
                connected={wsState === 'OPEN'}
                initialPath="/"
              />
            </div>
          )}
          <div className="sse-ai-chat-column">
            {editorOpen && openFile ? (
              <FileEditorPanel
                filePath={openFile.path}
                fileContent={openFile.content}
                fileSize={openFile.size}
                isDirty={isDirty}
                onClose={closeEditor}
                onSave={handleSaveFile}
              />
            ) : (
              <>
                <MessageList
                  onUserMessageResend={handleUserMessageResend}
                  onQuickCommand={handleQuickAction}
                  storageReady={storageReady}
                />
                <InputArea
                  onSend={handleSend}
                  onStop={handleStop}
                  onQuickAction={handleQuickAction}
                  storageReady={storageReady}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog onConfirm={handleConfirm} onReject={handleReject} />

      <InteractiveDialog onSubmit={handleInteractiveSubmit} onCancel={handleInteractiveCancel} />
    </div>
  );
};
